import { useCallback, useMemo, useState } from 'react';
import { Button, createTheme, TextInput } from 'flowbite-react';
import {
  AddExchangeRateRequest,
  ClientExchangeRateResponse,
  exchangeRateFormConfig,
  useCryptoCurrency,
} from '@acme/domains';
import {
  Form,
  FormControls,
  RangeDropdown,
  Separator,
  TabItem,
  Tabs,
  useFormModel,
  useModal,
  ResultModal,
  FormConfig,
} from '@acme/foundation';
import { Icon } from '@acme/assets';
import { CancelablePromise } from '@acme/api/shared';
import { useNavigate } from 'react-router-dom';
import { renderFieldIfText } from '@acme/foundation';
import { ExchangeRateTypeEnum } from '@acme/api';

interface IVolumeCourses {
  currencyVolume: { from: string; to: string } | null;
  volumeCourse: string;
}

const underlineTabsTheme = createTheme({
  tabs: {
    base: 'flex flex-col',
    tablist: {
      base: 'flex gap-8 items-center bg-white',
      tabitem: {
        base: 'flex items-center justify-center p-0 text-sm font-medium first:ml-0 focus:outline-none leading-[1.5]',
        variant: {
          default: {
            base: 'flex items-center justify-center pb-2',
            active: {
              on: 'border-b border-brand-purple-900 text-brand-purple-900 bg-white hover:bg-white leading-[1.5]',
              off: 'text-gray-500 leading-[1.5]',
            },
          },
        },
      },
    },
    tabpanel: '',
  },
}).tabs;

interface IExchangeRateFormProps {
  onClose: () => void;
  defaultData?: ClientExchangeRateResponse | null;
  onSubmit: (req: {
    requestBody?: AddExchangeRateRequest;
  }) => CancelablePromise<any>;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  adminMode?: boolean;
}

export const ExchangeRateForm = (props: IExchangeRateFormProps) => {
  const { currencies } = useCryptoCurrency();
  const { openModal, closeModal } = useModal();
  const navigate = useNavigate();

  const currenciesOptions = useMemo(() => {
    return currencies.map((i) => {
      return {
        label: i.code || '',
        value: String(i.id || ''),
      };
    });
  }, [currencies]);

  const formConfig = exchangeRateFormConfig(
    true,
    currenciesOptions
  ) as FormConfig<AddExchangeRateRequest>;

  const form = useFormModel<AddExchangeRateRequest>({
    config: formConfig,
    ctx: (props.defaultData
      ? {
          ...props.defaultData,
          cryptoCurrency: props.defaultData?.cryptoCurrency?.id,
        }
      : {}) as AddExchangeRateRequest,
  });

  const [volumeCourses, setVolumeCourses] = useState<IVolumeCourses[]>([]);
  const [isSell, setIsSell] = useState(false);
  const [currencyVolume, setCurrencyVolume] = useState<{
    from: string;
    to: string;
  } | null>(
    props.defaultData
      ? {
          from: String(props.defaultData?.rangeMin) || '',
          to: String(props.defaultData?.rangeMax) || '',
        }
      : null
  );
  const [volumeCourse, setVolumeCourse] = useState(
    String(props.defaultData?.price) || ''
  );

  const onSubmit = async (finalModel: AddExchangeRateRequest) => {
    try {
      const formData = props.adminMode
        ? {
            rateId: props.defaultData?.rateId,
            ranges: volumeCourses?.map((course) => {
              return {
                min: Number(course.currencyVolume?.from),
                max: Number(course.currencyVolume?.to),
                price: Number(course.volumeCourse),
              };
            }),
          }
        : ({
            type: isSell ? 200 : 100,
            // Рубль, Марат сказал захаркодить
            rateId: props.defaultData?.rateId,
            currencyId: 1,
            cryptoCurrency: Number(finalModel.cryptoCurrency),
            amount: Number(finalModel.amount),
            expireAt: new Date(finalModel.expireAt as string)?.toISOString(),
            ranges: volumeCourses?.map((course) => {
              return {
                min: Number(course.currencyVolume?.from),
                max: Number(course.currencyVolume?.to),
                price: Number(course.volumeCourse),
              };
            }),
          } as AddExchangeRateRequest);

      await props.onSubmit({ requestBody: formData }).then((res) => {
        closeModal();
        openModal({
          content: (
            <ResultModal
              title={res.isSuccess ? 'Успешно' : 'Добавить валюту не удалось'}
              description={
                res.isSuccess
                  ? 'Курс валюты был создан'
                  : 'Попробуйте повторить позднее'
              }
              type={res.isSuccess ? 'success' : 'error'}
              onBack={() => {
                navigate('/rates');
                closeModal();
              }}
            />
          ),
          opaqueBackground: true,
        });
      });
    } catch (error) {
      console.log(error);
      props.onError?.(
        error instanceof Error ? error.message : 'Ошибка при добавлении валюты'
      );
    }
  };

  const isSubmitDisabled = useMemo(() => {
    return volumeCourses.length === 0;
  }, [volumeCourses, form.state.values]);

  const handleClose = () => {
    props.onClose();
  };

  const isAddVolumeBtnDisabled = Boolean(
    volumeCourses?.length >= 3 || !(currencyVolume && volumeCourse)
  );

  const onAddVolumeClick = useCallback(() => {
    if (isAddVolumeBtnDisabled) return;
    setVolumeCourses((prev) => [...prev, { volumeCourse, currencyVolume }]);
  }, [isAddVolumeBtnDisabled, currencyVolume, volumeCourse]);

  const handleRemove = (indexToRemove: number) => {
    setVolumeCourses((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const renderForm = () => {
    return (
      <div className="flex flex-col gap-6">
        {props.adminMode ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              <div className="text-base font-medium text-gray-900">
                {props.defaultData?.organization?.name}
              </div>
              <div className="flex gap-2 py-0.5 px-2.5 rounded-full bg-green-100 items-center w-fit">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span className="text-xs text-green-800">
                  {props.defaultData?.type === ExchangeRateTypeEnum.Buy
                    ? 'Покупка валюты'
                    : 'Продажа валюты'}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {renderFieldIfText(
                'Валюта',
                props.defaultData?.cryptoCurrency?.name
              )}{' '}
              {renderFieldIfText(
                'Общий объем валюты',
                String(props.defaultData?.amount)
              )}
              {renderFieldIfText(
                'Время действия курса',
                new Intl.DateTimeFormat('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
                  .format(new Date(props.defaultData?.expireAt as string))
                  .replace(/\//g, '.')
              )}
            </div>
            <Separator />
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <FormControls form={form} />
          </div>
        )}
        <div className="flex flex-col gap-4">
          <div className="text-gray-500 text-sm font-medium">
            Можно установить курс для трех объемов валюты
          </div>
          <div className="grid grid-cols-2 gap-3">
            <RangeDropdown
              label="Объём для курса"
              placeholder="Выберите объем"
              onChange={(val) => setCurrencyVolume(val)}
              value={currencyVolume}
            />
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium text-gray-900">
                Курс для объема
              </div>
              <TextInput
                id="from-input"
                type="number"
                placeholder="Введите курс"
                value={volumeCourse}
                onChange={(e) => setVolumeCourse(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6 leading-none text-base font-medium">
          <div
            className={`w-fit ${isAddVolumeBtnDisabled ? 'cursor-not-allowed text-brand-purple-500' : 'cursor-pointer text-brand-purple-900'}`}
            onClick={onAddVolumeClick}
          >
            Установить курс для объема
          </div>
          {Array.isArray(volumeCourses) && volumeCourses.length > 0 && (
            <div className="flex gap-2 text-gray-900">
              Добавляемые объемы
              <div className="text-gray-500">{`${volumeCourses?.length} из 3`}</div>
            </div>
          )}
          {volumeCourses?.map((course, index) => (
            <div key={index} className="flex gap-12">
              <div className="flex gap-2 text-gray-500">
                Объем:
                <div className="text-gray-900">{`От ${course.currencyVolume?.from} до ${course.currencyVolume?.to}`}</div>
              </div>
              <div className="flex gap-2 text-gray-500">
                Курс:<div className="text-gray-900">{course.volumeCourse}</div>
              </div>
              <div
                onClick={() => handleRemove(index)}
                className="text-red-600 hover:text-red-800 hover:underline cursor-pointer ml-auto"
              >
                Удалить
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const tabItems: TabItem[] = [
    {
      key: 'buy',
      title: 'Покупка валюты',
      content: renderForm(),
    },
    {
      key: 'sell',
      title: 'Продажа валюты',
      content: renderForm(),
    },
  ];

  return (
    <Form<AddExchangeRateRequest>
      form={form}
      onSubmit={onSubmit}
      className="flex flex-col"
    >
      <div className="flex pb-5">
        <h3 className="text-gray-900 text-lg font-semibold">
          {props.adminMode ? 'Корректировка курса' : 'Добавление валюты'}
        </h3>
        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600 ml-auto"
        >
          <Icon size={10.5} name="x-outline" />
        </button>
      </div>
      <Separator className="-mx-5" />
      <div
        className="py-6"
        onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
      >
        {props.adminMode ? (
          renderForm()
        ) : (
          <Tabs
            items={tabItems}
            defaultActiveKey="buy"
            onTabChange={(key) => setIsSell(key === 'sell')}
            theme={underlineTabsTheme}
          />
        )}
      </div>
      <Separator className="-mx-5" />
      <div className="flex gap-4 pt-5">
        <Button type="submit" color="primary" disabled={isSubmitDisabled}>
          Добавить валюту
        </Button>
        <Button color="gray" onClick={handleClose}>
          Отмена
        </Button>
      </div>
    </Form>
  );
};
